import { Popover } from '@amzn/awsui-components-core';
import { faCalendarDays } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useState } from 'react';
import { logger } from 'src/logger';
import { fetchWorkDayCalendar, WorkDayCalendarEntry } from 'src/services/WorkDayCalendarService';
import WorkDayCalendar from 'src/components/generic-components/workday-calendar/WorkDayCalendar';
import { QUARTER_END_MONTHS as QUARTER_END_MONTH_LIST } from 'src/components/dfp-home-page/landing-page/fiscal-quarter-constants';
import { getQuarterProgress, QuarterProgress } from 'src/services/QuarterProgressService';

const QUARTER_END_MONTHS = new Set<number>(QUARTER_END_MONTH_LIST);
const NON_QUARTER_END_CAP = 10;

const formatMecStartDate = (dateKey: string): string => {
  const [year, month, day] = dateKey.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return `starts ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
};

interface NextWorkDay {
  workDayNumber: number;
  dateKey: string;
}

interface MecWorkDayWidgetProps {
  dismissPopover?: number;
}

const MecWorkDayWidget: React.FC<MecWorkDayWidgetProps> = ({ dismissPopover }) => {
  const [data, setData] = useState<WorkDayCalendarEntry[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = 'mec-workday-calendar';
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        setData(JSON.parse(cached));
        return;
      } catch {
        /* fall through to fetch */
      }
    }
    fetchWorkDayCalendar()
      .then((entries) => {
        if (cancelled) return;
        if (!entries.length) {
          logger.error('[MecWorkDayWidget] No calendar entries found in S3');
          return;
        }
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(entries));
        } catch {
          /* ignore cache write failure */
        }
        setData(entries);
      })
      .catch((err) => {
        if (!cancelled) logger.error('[MecWorkDayWidget] Failed to fetch work day calendar', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const workDayMap = useMemo(() => {
    const map = new Map<string, number>();
    (data ?? []).forEach((e) => {
      if (e.work_day_number <= 0) return;
      const dateKey = e.calendar_date.slice(0, 10);
      const month = parseInt(dateKey.slice(5, 7), 10);
      if (!QUARTER_END_MONTHS.has(month) && e.work_day_number > NON_QUARTER_END_CAP) return;
      map.set(dateKey, e.work_day_number);
    });
    return map;
  }, [data]);

  const todayKey = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  }, []);
  const todayWD = workDayMap.get(todayKey);

  const nextWorkDay = useMemo((): NextWorkDay | null => {
    if (todayWD || !data) return null;
    const today = new Date(todayKey);
    let nearest: NextWorkDay | null = null;
    for (const entry of data) {
      if (entry.work_day_number <= 0) continue;
      const entryDate = new Date(entry.calendar_date.slice(0, 10));
      if (entryDate > today) {
        const month = parseInt(entry.calendar_date.slice(5, 7), 10);
        if (!QUARTER_END_MONTHS.has(month) && entry.work_day_number > NON_QUARTER_END_CAP) continue;
        if (!nearest || entryDate < new Date(nearest.dateKey)) {
          nearest = { workDayNumber: entry.work_day_number, dateKey: entry.calendar_date.slice(0, 10) };
        }
      }
    }
    return nearest;
  }, [todayWD, data, todayKey]);

  const displayWorkDay = todayWD ?? nextWorkDay?.workDayNumber ?? null;

  const mecStartDate = useMemo(() => {
    if (todayWD !== 1 || !data) return null;
    const todayMonth = todayKey.slice(0, 7);
    const wd1Entry = data.find((e) => e.work_day_number === 1 && e.calendar_date.slice(0, 7) === todayMonth);
    return wd1Entry ? wd1Entry.calendar_date.slice(0, 10) : null;
  }, [todayWD, data, todayKey]);

  const subtitle = useMemo(() => {
    if (todayWD) {
      if (mecStartDate) return formatMecStartDate(mecStartDate);
      return 'in progress';
    }
    if (nextWorkDay) return formatMecStartDate(nextWorkDay.dateKey);
    return null;
  }, [todayWD, mecStartDate, nextWorkDay]);

  const quarterProgress: QuarterProgress = useMemo(() => getQuarterProgress(), []);

  if (!data) return null;

  const dotClass = todayWD ? 'mec-workday-widget__dot--active' : 'mec-workday-widget__dot--starting';

  return (
    <div className="mec-workday-widget" data-testid="mec-workday-widget">
      <Popover key={dismissPopover} position="bottom" size="large" triggerType="custom" content={<WorkDayCalendar workDayMap={workDayMap} />}>
        <button
          className="mec-workday-widget__trigger"
          data-testid="mec-workday-trigger"
          aria-label={`Open MEC Calendar${displayWorkDay ? `, currently work day ${displayWorkDay}` : ''}`}
        >
          <FontAwesomeIcon icon={faCalendarDays} />
        </button>
      </Popover>
      {displayWorkDay && (
        <>
          <span className={`mec-workday-widget__dot ${dotClass}`} data-testid="mec-workday-dot" />
          <div className="mec-workday-widget__info" data-testid="mec-workday-info">
            <span className="mec-workday-widget__label" data-testid="mec-workday-label">
              MEC Work Day {displayWorkDay}
            </span>
            {subtitle && (
              <span className="mec-workday-widget__subtitle" data-testid="mec-workday-subtitle">
                {subtitle}
              </span>
            )}
          </div>
        </>
      )}
      <span className="mec-workday-widget__separator" />
      <div className="mec-workday-widget__info" data-testid="quarter-progress-info">
        <span className="mec-workday-widget__label" data-testid="quarter-progress-label">
          {quarterProgress.quarter} · {quarterProgress.percentComplete}%
        </span>
        <span className="mec-workday-widget__subtitle" data-testid="quarter-progress-subtitle">
          {quarterProgress.daysRemaining} days remaining
        </span>
      </div>
    </div>
  );
};

export default MecWorkDayWidget;
